import { captureException } from '@sentry/browser';
import { AxiosError } from 'axios';
import type { NextPage, NextPageContext } from 'next';

import { wrapperReduxToolkit } from '@/store';
import { loadDefaultData } from '@/store/utils/load-default-data';

import { Footer } from '@/widgets/footer';
import { Header } from '@/widgets/header';

import {
    TArticle,
    MAIN_ARTICLES_PAGE_TYPES,
    getArticlesWithMainData,
    ArticlesListPageModule,
} from '@/entities/article';
import { HeadMeta, getMetaDataPage } from '@/entities/meta-seo';

import { NOT_FOUND_PAGE, SERVER_ERROR, STATUS_CODE_OK } from '@/shared/constants/settings';
import { Layout } from '@/shared/ui/layout';
import { CatchingErrorLayout } from '@/shared/ui/layout/catching-error';
import { getNumberFromString } from '@/shared/utils/querify';

type TProps = {
    articles: TArticle[];
    totalPage: number;
    currentPage: number;
    mainArticle: TArticle;
    typePage: MAIN_ARTICLES_PAGE_TYPES;
    statusCode: number;
    title?: string;
    description?: string;
};

const Page: NextPage<TProps> = ({ articles, currentPage, totalPage, statusCode, mainArticle, description, title }) => {
    return (
        <CatchingErrorLayout statusCode={statusCode}>
            <Layout header={<Header />} footer={<Footer />}>
                <HeadMeta title={title} description={description} />
                <ArticlesListPageModule
                    articles={articles}
                    mainArticle={mainArticle}
                    currentPage={currentPage}
                    totalPage={totalPage}
                />
            </Layout>
        </CatchingErrorLayout>
    );
};

Page.getInitialProps = wrapperReduxToolkit.getInitialPageProps(
    ({ dispatch, getState }) =>
        async (context: NextPageContext) => {
            const { locale, res, query } = context;
            // Загрузка стора при серверном и клиенском рединге, общих настроек

            await loadDefaultData({
                getState,
                dispatch,
                locale: locale as string,
                context,
            });

            let statusCode = STATUS_CODE_OK;

            try {
                const response = await Promise.all([
                    getArticlesWithMainData(getNumberFromString(query.page as string), locale),
                    getMetaDataPage('/articles', locale as string),
                ]);

                if (res) {
                    res.statusCode = response[0].statusCode;
                }

                return {
                    ...response[0],
                    ...response[1],
                    statusCode: response[0].statusCode,
                };
            } catch (err) {
                const error = err as AxiosError;
                statusCode = error.response?.status === NOT_FOUND_PAGE ? NOT_FOUND_PAGE : SERVER_ERROR;
                captureException(error);
            }

            if (res) {
                res.statusCode = statusCode;
            }

            return { statusCode };
        },
);

export default Page;
